import React, { useState, useEffect } from "react";
import { Plus, Trash2, Save, X, Languages, Sparkles } from "lucide-react";
import { api, formatRON } from "../lib/shop";
import { toast } from "sonner";
import RichTextEditor from "./RichTextEditor";
import ImageUploader from "./ImageUploader";

const EMPTY = {
  name: "",
  short_name: "",
  slug: "",
  category: "scule",
  price: 0,
  old_price: null,
  description: "",
  description_html: "",
  highlights: [],
  specs: {},
  images: [],
  quantity_discount_tiers: [],
  rating: null,
  reviews: null,
  translations: { en: null, it: null },
};

const LANG_TABS = [
  { code: "ro", flag: "🇷🇴", label: "Română (sursă)" },
  { code: "en", flag: "🇬🇧", label: "English" },
  { code: "it", flag: "🇮🇹", label: "Italiano" },
];

const emptyTranslation = () => ({ name: "", short_name: "", description: "", description_html: "", highlights: [], specs: {} });

export default function ProductForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial ? { ...EMPTY, ...initial, translations: { ...(EMPTY.translations), ...(initial.translations || {}) } } : EMPTY);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("ro");
  const [highlight, setHighlight] = useState("");
  const [specKey, setSpecKey] = useState("");
  const [specVal, setSpecVal] = useState("");
  const [trans, setTrans] = useState({ highlight: "", specKey: "", specVal: "" });
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (initial) setForm({ ...EMPTY, ...initial, translations: { ...(EMPTY.translations), ...(initial.translations || {}) } });
  }, [initial]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTr = (lang, k, v) => setForm((f) => ({
    ...f,
    translations: { ...(f.translations || {}), [lang]: { ...(f.translations?.[lang] || emptyTranslation()), [k]: v } },
  }));

  // RO list helpers
  const addHighlight = () => {
    const v = highlight.trim();
    if (!v) return;
    setF("highlights", [...(form.highlights || []), v]);
    setHighlight("");
  };
  const removeHighlight = (i) => setF("highlights", form.highlights.filter((_, idx) => idx !== i));

  const addSpec = () => {
    const k = specKey.trim(); const v = specVal.trim();
    if (!k || !v) return;
    setF("specs", { ...(form.specs || {}), [k]: v });
    setSpecKey(""); setSpecVal("");
  };
  const removeSpec = (k) => {
    const next = { ...(form.specs || {}) }; delete next[k]; setF("specs", next);
  };

  // Translation list helpers
  const addTrHighlight = (lang) => {
    const v = trans.highlight.trim(); if (!v) return;
    const cur = form.translations?.[lang]?.highlights || [];
    setTr(lang, "highlights", [...cur, v]);
    setTrans({ ...trans, highlight: "" });
  };
  const removeTrHighlight = (lang, i) => {
    const cur = form.translations?.[lang]?.highlights || [];
    setTr(lang, "highlights", cur.filter((_, idx) => idx !== i));
  };
  const addTrSpec = (lang) => {
    const k = trans.specKey.trim(); const v = trans.specVal.trim(); if (!k || !v) return;
    const cur = form.translations?.[lang]?.specs || {};
    setTr(lang, "specs", { ...cur, [k]: v });
    setTrans({ ...trans, specKey: "", specVal: "" });
  };
  const removeTrSpec = (lang, k) => {
    const cur = { ...(form.translations?.[lang]?.specs || {}) }; delete cur[k];
    setTr(lang, "specs", cur);
  };

  const addTier = () => setF("quantity_discount_tiers", [...(form.quantity_discount_tiers || []), { qty: 5, percent: 5 }]);
  const updateTier = (i, patch) => setF("quantity_discount_tiers", form.quantity_discount_tiers.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const removeTier = (i) => setF("quantity_discount_tiers", form.quantity_discount_tiers.filter((_, idx) => idx !== i));

  // Auto-translate current product (must have a saved id)
  const autoTranslate = async () => {
    if (!initial?.id) {
      toast.error("Salvează produsul mai întâi, apoi rulează traducerea automată.");
      return;
    }
    setTranslating(true);
    try {
      const { data } = await api.post(`/admin/products/${initial.id}/translate`);
      setForm((f) => ({ ...f, translations: data.translations || f.translations }));
      toast.success("Tradus în EN + IT");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la traducere");
    } finally { setTranslating(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.short_name || !form.price) {
      toast.error("Completează numele, denumirea scurtă și prețul"); return;
    }
    if (!form.images.length) {
      toast.error("Adaugă cel puțin o imagine"); return;
    }
    setSaving(true);
    try {
      // strip null translations so backend doesn't store empties
      const tr = form.translations || {};
      const cleanedTr = {};
      for (const lang of ["en", "it"]) {
        const v = tr[lang];
        if (v && (v.name || v.short_name || v.description || v.description_html || (v.highlights || []).length || Object.keys(v.specs || {}).length)) {
          cleanedTr[lang] = v;
        }
      }
      const payload = {
        ...form,
        price: parseFloat(form.price),
        old_price: form.old_price ? parseFloat(form.old_price) : null,
        rating: form.rating ? parseFloat(form.rating) : null,
        reviews: form.reviews ? parseInt(form.reviews, 10) : null,
        quantity_discount_tiers: (form.quantity_discount_tiers || []).map((t) => ({ qty: parseInt(t.qty, 10), percent: parseFloat(t.percent) })),
        description: form.description_html ? form.description_html.replace(/<[^>]*>/g, "").trim().slice(0, 600) : form.description,
        translations: Object.keys(cleanedTr).length ? cleanedTr : null,
      };
      if (initial?.id) {
        await api.patch(`/admin/products/${initial.id}`, payload);
        toast.success("Produs actualizat");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produs creat");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la salvare");
    } finally { setSaving(false); }
  };

  const label = "block text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2 font-bold";
  const input = "w-full border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-gray-900 focus:outline-none";

  // ─── Render RO source form (existing fields) ───
  const renderRO = () => (
    <>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="sm:col-span-2">
          <label className={label}>Nume complet (afișat pe pagina produsului)</label>
          <input className={input} value={form.name} onChange={(e) => setF("name", e.target.value)} required data-testid="form-name" />
        </div>
        <div>
          <label className={label}>Denumire scurtă (titlu coș & card)</label>
          <input className={input} value={form.short_name} onChange={(e) => setF("short_name", e.target.value)} required data-testid="form-short-name" />
        </div>
        <div>
          <label className={label}>Slug URL <span className="text-gray-400 normal-case tracking-normal">(opțional · auto-generat)</span></label>
          <input className={input + " font-mono"} value={form.slug || ""} onChange={(e) => setF("slug", e.target.value)} placeholder="ex: set-cutite-japonez" data-testid="form-slug" />
        </div>
        <div>
          <label className={label}>Categorie</label>
          <select className={input} value={form.category} onChange={(e) => setF("category", e.target.value)} required data-testid="form-category">
            <option value="cutite">Cuțite Profesionale</option>
            <option value="scule">Truse de Scule</option>
          </select>
        </div>
        <div>
          <label className={label}>Preț (RON)</label>
          <input type="number" step="0.01" className={input + " font-mono"} value={form.price} onChange={(e) => setF("price", e.target.value)} required data-testid="form-price" />
          {form.price > 0 && <p className="mt-1 text-[11px] text-gray-500">Afișat: {formatRON(parseFloat(form.price) || 0)}</p>}
        </div>
        <div>
          <label className={label}>Preț vechi (opțional · pentru badge reducere)</label>
          <input type="number" step="0.01" className={input + " font-mono"} value={form.old_price ?? ""} onChange={(e) => setF("old_price", e.target.value || null)} data-testid="form-old-price" />
        </div>
        <div>
          <label className={label}>Rating (1-5, opțional)</label>
          <input type="number" step="0.01" min="0" max="5" className={input + " font-mono"} value={form.rating ?? ""} onChange={(e) => setF("rating", e.target.value || null)} data-testid="form-rating" />
        </div>
        <div>
          <label className={label}>Număr recenzii (opțional)</label>
          <input type="number" min="0" className={input + " font-mono"} value={form.reviews ?? ""} onChange={(e) => setF("reviews", e.target.value || null)} data-testid="form-reviews" />
        </div>
      </div>

      <div>
        <label className={label}>Imagini produs <span className="text-gray-400 normal-case tracking-normal">(prima = principală)</span></label>
        <ImageUploader value={form.images} onChange={(imgs) => setF("images", imgs)} />
      </div>

      <div>
        <label className={label}>Descriere produs <span className="text-gray-400 normal-case tracking-normal">(formatare: titluri, culori, fundaluri)</span></label>
        <RichTextEditor value={form.description_html} onChange={(html) => setF("description_html", html)} />
      </div>

      <div>
        <label className={label}>Puncte forte (bullets)</label>
        <div className="flex gap-2 mb-3">
          <input className={input + " flex-1"} value={highlight} onChange={(e) => setHighlight(e.target.value)} placeholder="ex: Oțel japonez HRC 60"
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHighlight())} data-testid="form-highlight-input" />
          <button type="button" onClick={addHighlight} className="border border-gray-900 px-4 text-xs font-bold uppercase tracking-wider hover:bg-gray-900 hover:text-white"><Plus size={14} /></button>
        </div>
        <ul className="space-y-1.5">
          {(form.highlights || []).map((h, i) => (
            <li key={i} className="flex items-center justify-between border border-gray-200 px-3 py-2 text-sm" data-testid={`highlight-${i}`}>
              <span>• {h}</span>
              <button type="button" onClick={() => removeHighlight(i)} className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <label className={label}>Specificații (cheie / valoare)</label>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 mb-3">
          <input className={input} value={specKey} onChange={(e) => setSpecKey(e.target.value)} placeholder="ex: Material lamă" data-testid="form-spec-key" />
          <input className={input} value={specVal} onChange={(e) => setSpecVal(e.target.value)} placeholder="ex: Oțel japonez" data-testid="form-spec-val" />
          <button type="button" onClick={addSpec} className="border border-gray-900 px-4 text-xs font-bold uppercase tracking-wider hover:bg-gray-900 hover:text-white">Adaugă</button>
        </div>
        <ul className="divide-y divide-gray-200 border border-gray-200">
          {Object.entries(form.specs || {}).map(([k, v]) => (
            <li key={k} className="grid grid-cols-[1fr_1fr_auto] gap-3 px-3 py-2 text-sm items-center" data-testid={`spec-${k}`}>
              <span className="text-gray-500">{k}</span>
              <span className="font-medium">{v}</span>
              <button type="button" onClick={() => removeTrSpec ? removeSpec(k) : removeSpec(k)} className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <label className={label}>Reducere pe cantitate (opțional)</label>
        <p className="text-[11px] text-gray-500 mb-3">Exemplu: 5 buc → 5% reducere · 10 buc → 10% reducere</p>
        <div className="space-y-2 mb-3">
          {(form.quantity_discount_tiers || []).map((tt, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2" data-testid={`tier-${i}`}>
              <div><input type="number" min="1" className={input + " font-mono"} value={tt.qty} onChange={(e) => updateTier(i, { qty: e.target.value })} placeholder="Cantitate" /></div>
              <div><input type="number" step="0.5" min="0" max="100" className={input + " font-mono"} value={tt.percent} onChange={(e) => updateTier(i, { percent: e.target.value })} placeholder="% reducere" /></div>
              <button type="button" onClick={() => removeTier(i)} className="text-gray-400 hover:text-red-600 px-2"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addTier} className="text-xs uppercase tracking-wider border border-gray-300 px-4 py-2 hover:border-gray-900"><Plus size={12} className="inline mr-1" /> Adaugă tier</button>
      </div>
    </>
  );

  // ─── Render translation form for EN or IT ───
  const renderTranslation = (lang) => {
    const T = form.translations?.[lang] || emptyTranslation();
    const flag = lang === "en" ? "🇬🇧" : "🇮🇹";
    const langName = lang === "en" ? "English" : "Italiano";

    return (
      <div className="space-y-6">
        <div className="border border-dashed border-gray-300 bg-gray-50/40 px-4 py-3 flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-gray-600 flex items-center gap-2">
            <Sparkles size={14} className="text-[#1ebe57]" />
            <span>Auto-traducere AI a tuturor câmpurilor RO către <span className="font-bold">{flag} {langName}</span>. Apasă „Tradu automat" pentru a popula instant.</span>
          </div>
          <button type="button" onClick={autoTranslate} disabled={translating || !initial?.id}
            className="border border-gray-900 px-4 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-gray-900 hover:text-white transition disabled:opacity-50 inline-flex items-center gap-2"
            data-testid={`autotranslate-${lang}`}
          >
            <Languages size={12} /> {translating ? "Se traduce..." : "Tradu automat (EN + IT)"}
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <label className={label}>Nume complet ({langName})</label>
            <input className={input} value={T.name || ""} onChange={(e) => setTr(lang, "name", e.target.value)} data-testid={`tr-${lang}-name`} />
          </div>
          <div className="sm:col-span-2">
            <label className={label}>Denumire scurtă ({langName})</label>
            <input className={input} value={T.short_name || ""} onChange={(e) => setTr(lang, "short_name", e.target.value)} data-testid={`tr-${lang}-short-name`} />
          </div>
        </div>

        <div>
          <label className={label}>Descriere scurtă ({langName})</label>
          <textarea className={input + " min-h-[100px]"} value={T.description || ""} onChange={(e) => setTr(lang, "description", e.target.value)} data-testid={`tr-${lang}-description`} />
        </div>

        <div>
          <label className={label}>Descriere completă HTML ({langName})</label>
          <RichTextEditor value={T.description_html || ""} onChange={(html) => setTr(lang, "description_html", html)} />
        </div>

        <div>
          <label className={label}>Puncte forte ({langName})</label>
          <div className="flex gap-2 mb-3">
            <input className={input + " flex-1"} value={trans.highlight} onChange={(e) => setTrans({ ...trans, highlight: e.target.value })} placeholder="Add a highlight..."
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTrHighlight(lang))} />
            <button type="button" onClick={() => addTrHighlight(lang)} className="border border-gray-900 px-4 text-xs font-bold uppercase tracking-wider hover:bg-gray-900 hover:text-white"><Plus size={14} /></button>
          </div>
          <ul className="space-y-1.5">
            {(T.highlights || []).map((h, i) => (
              <li key={i} className="flex items-center justify-between border border-gray-200 px-3 py-2 text-sm">
                <span>• {h}</span>
                <button type="button" onClick={() => removeTrHighlight(lang, i)} className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <label className={label}>Specificații ({langName})</label>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 mb-3">
            <input className={input} value={trans.specKey} onChange={(e) => setTrans({ ...trans, specKey: e.target.value })} placeholder="ex: Blade material" />
            <input className={input} value={trans.specVal} onChange={(e) => setTrans({ ...trans, specVal: e.target.value })} placeholder="ex: Japanese steel" />
            <button type="button" onClick={() => addTrSpec(lang)} className="border border-gray-900 px-4 text-xs font-bold uppercase tracking-wider hover:bg-gray-900 hover:text-white">Add</button>
          </div>
          <ul className="divide-y divide-gray-200 border border-gray-200">
            {Object.entries(T.specs || {}).map(([k, v]) => (
              <li key={k} className="grid grid-cols-[1fr_1fr_auto] gap-3 px-3 py-2 text-sm items-center">
                <span className="text-gray-500">{k}</span>
                <span className="font-medium">{v}</span>
                <button type="button" onClick={() => removeTrSpec(lang, k)} className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="product-form">
      {/* Language tabs */}
      <div className="border-b border-gray-200 flex gap-1" data-testid="form-lang-tabs">
        {LANG_TABS.map((l) => {
          const active = tab === l.code;
          const hasTr = l.code === "ro" || (form.translations?.[l.code] && (form.translations[l.code].name || form.translations[l.code].short_name));
          return (
            <button
              key={l.code}
              type="button"
              onClick={() => setTab(l.code)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.2em] border-b-2 -mb-px transition ${active ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900'}`}
              data-testid={`form-tab-${l.code}`}
            >
              <span aria-hidden="true">{l.flag}</span>
              <span>{l.label}</span>
              {l.code !== "ro" && (
                <span className={`text-[9px] px-1 py-0.5 border ${hasTr ? 'border-green-500 text-green-700' : 'border-gray-300 text-gray-400'}`}>
                  {hasTr ? "✓" : "—"}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "ro" && renderRO()}
      {tab === "en" && renderTranslation("en")}
      {tab === "it" && renderTranslation("it")}

      <div className="flex items-center gap-3 pt-6 border-t border-gray-200">
        <button type="submit" disabled={saving} className="bg-gray-900 text-white px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gray-800 disabled:opacity-50 inline-flex items-center gap-2" data-testid="form-submit">
          <Save size={14} /> {saving ? "Se salvează..." : initial?.id ? "Actualizează produs" : "Creează produs"}
        </button>
        <button type="button" onClick={onCancel} className="border border-gray-300 px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:border-gray-900 inline-flex items-center gap-2" data-testid="form-cancel">
          <X size={14} /> Anulează
        </button>
      </div>
    </form>
  );
}
